import { once } from 'lodash';

/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import PropTypes from 'prop-types';

import React, { Component } from 'react';

import hoistNonReactInstanceFunctions from './hoist-non-react-instance-functions';

type Imports = Record<string, string>;

/**
 * A HOC that will ensure AMD modules are loaded. Use of this HOC requires an AMD loader is available at runtime.
 *
 * The initializer will be called only once when all modules have loaded.
 *
 * The HOC will mount the component immediately unless mountOnLoad is 'true'.
 *
 * @param {Object} imports - key value pair of import name to require path
 * @param {Function=} initlaizer - an optional function which will be called once all imports have been resolved
 * @param {Boolean} mountOnLoad - if true, the wrapped component will only mount once all imports have resolved,
 *                                otherwise the wrapped component will mount immediately
 */
export default function(imports: Imports, initializer?: (any) => void, mountOnLoad?: boolean) {
	let resolved;
	let promise;
	const names = [];
	const paths = [];

	Object.keys(imports).forEach(key => {
		paths.push(imports[key]);
		names.push(key);
	});

	function doRequire(instance) {
		if (!resolved) {
			if (!promise) {
				promise = new Promise(resolve => {
					window.require(paths, (...args) => {
						resolved = {};
						promise = null;
						names.forEach((name, i) => {
							resolved[name] = args[i];
						});
						initializer && initializer(resolved);
						resolve(resolved);
					});
				});
			}
			promise.then(() => {
				instance.forceUpdateOnce();
			});
		}
	}

	return function(WrappedComponent) {
		class RequireAMD extends Component<any, any> {
			static propTypes = {
				modulesLoaded: PropTypes.bool,
			};

			constructor(props) {
				super(props);
				this._doLoad();
				this.forceUpdateOnce = once(() => this.forceUpdate());
			}

			componentDidUpdate() {
				this._doLoad();
			}

			forceUpdateOnce: () => void;
			_resolved: {};
			wrappedComponent: Component;
			static WrappedComponent: Component;

			_doLoad() {
				if (this._isModulesLoaded() && !this._getResolved()) {
					doRequire(this);
				}
			}

			_isModulesLoaded() {
				return this.props.modulesLoaded !== false;
			}

			_getResolved() {
				if (!this._resolved) {
					if (!resolved) {
						const resolvedModules = {};
						const allResolved = Object.keys(imports).every(key => {
							if (!Object.prototype.hasOwnProperty.call(this.props, key)) {
								return false;
							}
							resolvedModules[key] = this.props[key];

							return true;
						});

						if (allResolved) {
							this._resolved = resolvedModules;
						}
					} else {
						this._resolved = resolved;
					}
				}

				return this._resolved;
			}

			_ref = wc => {
				this.wrappedComponent = wc;
				if (wc) {
					hoistNonReactInstanceFunctions(this, wc);
					this._isModulesLoaded() && this._doLoad();
				}
			};

			render() {
				const modulesLoaded = this._isModulesLoaded() && !!this._getResolved();

				if (!mountOnLoad || modulesLoaded) {
					return (
						<WrappedComponent
							{...this.props}
							{...resolved}
							ref={this._ref}
							modulesLoaded={modulesLoaded}
						/>
					);
				}

				return null;
			}
		}
		RequireAMD.WrappedComponent = WrappedComponent;

		return RequireAMD as any;
	};
}

// Use this mixin to supply a new lifecycle function called 'componentDidLoadModules'
// This lifecycle function will be called once all modules have been loaded via requireAMD HOC.
export function mixinComponentDidLoadModules(WrappedComponent) {
	const proto = WrappedComponent.prototype;

	if (proto.componentDidLoadModules) {
		const oriComponentDidMount = proto.componentDidMount;
		const oriComponentDidUpdate = proto.componentDidUpdate;

		proto.componentDidMount = function(...args) {
			// by checking !== false, tests can pass as props the mock modules and not have to also include modulesLoaded={true}
			if (this.props.modulesLoaded !== false) {
				this.componentDidLoadModules();
			}
			oriComponentDidMount && oriComponentDidMount.apply(this, args);
		};

		proto.componentDidUpdate = function(...args) {
			const [prevProps] = args;

			if (!prevProps.modulesLoaded && this.props.modulesLoaded) {
				this.componentDidLoadModules();
			}
			oriComponentDidUpdate && oriComponentDidUpdate.apply(this, args);
		};
	}
}
